import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  IconButton,
} from '@material-ui/core';
import { RemoveRedEye, ExpandMore, ExpandLess } from '@material-ui/icons';

import { formatMoneyAuto } from 'utils/formatMoney';
import { getVenueCatalog, exportVenueCatalog } from 'services/stockServices';
import { StockCatalogProduct, StockCatalogPagination } from 'types/models/StockReceipt';
import StockCatalogForm, { StockCatalogFilters } from 'forms/StockCatalogForm';
import 'styles/pages/StockCatalogPage.scss';

type StockStatus = 'CRITICAL' | 'MINIMUM' | 'OK';

const getStockStatus = (product: StockCatalogProduct): StockStatus => {
  if (product.minimumStock !== null && product.totalStock <= product.minimumStock) return 'CRITICAL';
  if (product.safetyStock !== null && product.totalStock <= product.safetyStock) return 'MINIMUM';
  return 'OK';
};

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const [filters, setFilters] = React.useState<StockCatalogFilters | null>(null);
  const [catalog, setCatalog] = React.useState<StockCatalogProduct[]>([]);
  const [totalValueInventoryCpp, setTotalValueInventoryCpp] = React.useState(0);
  const [pagination, setPagination] = React.useState<StockCatalogPagination | null>(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = React.useState(false);
  const [summary, setSummary] = React.useState<{ alert: number; critical: number; minimum: number; ok: number }>({
    alert: 0,
    critical: 0,
    minimum: 0,
    ok: 0,
  });
  const [isExporting, setIsExporting] = React.useState(false);
  const [expandedId, setExpandedId] = React.useState<number | null>(null);

  const loadCatalog = React.useCallback(async (searchFilters: StockCatalogFilters, page: number) => {
    setIsLoadingCatalog(true);

    try {
      const response = await getVenueCatalog(
        searchFilters.venueId,
        searchFilters.warehouseIds,
        page,
        searchFilters.searchTerm,
        searchFilters.status,
      );

      setCatalog(response.products);
      setTotalValueInventoryCpp(response.totalValueInventoryCpp);
      setSummary({
        alert: response.alertCount,
        critical: response.criticalCount,
        minimum: response.minimumCount,
        ok: response.okCount,
      });
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCatalog(false);
    }
  }, []);

  const handleSearch = (searchFilters: StockCatalogFilters) => {
    setFilters(searchFilters);
    loadCatalog(searchFilters, 1);
  };

  const handlePageChange = (page: number) => {
    if (!filters) return;
    loadCatalog(filters, page);
  };

  const handleExport = async (searchFilters: StockCatalogFilters) => {
    if (!searchFilters.venueId || isExporting) return;

    setIsExporting(true);
    try {
      const blob = await exportVenueCatalog(
        searchFilters.venueId,
        searchFilters.warehouseIds,
        searchFilters.searchTerm,
        searchFilters.status,
      );

      if (!blob) {
        console.error('Export failed');
        return;
      }

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      a.download = `catalogo_stock_${searchFilters.venueId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const hasSearched = filters !== null;

  const renderBreakdown = (product: StockCatalogProduct) => (
    <div className="catalog-breakdown">
      <div className="catalog-breakdown__group">
        <Typography variant="subtitle2" gutterBottom>
          {t('stock.catalog.warehouses')}
        </Typography>
        {product.warehouses.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        ) : (
          <ul className="catalog-breakdown__list">
            {product.warehouses.map((w) => (
              <li key={w.id}>
                <span>{w.name}</span>
                <strong>{w.stock}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="catalog-breakdown__group">
        <Typography variant="subtitle2" gutterBottom>
          {t('stock.catalog.commerces')}
        </Typography>
        {product.commerces.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        ) : (
          <ul className="catalog-breakdown__list">
            {product.commerces.map((c) => (
              <li key={c.id}>
                <span>{c.name}</span>
                <strong>{c.stock}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      <StockCatalogForm
        onSearch={handleSearch}
        onExport={handleExport}
        isLoading={isLoadingCatalog}
        isExporting={isExporting}
      />

      {isLoadingCatalog && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <CircularProgress />
        </div>
      )}

      {hasSearched && !isLoadingCatalog && (
        <>
          <TableContainer component={Paper} className="table-container" style={{ marginTop: 20 }}>
            <Table>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell />
                  <TableCell>Producto</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">{t('stock.global-products.average-cost')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.minimum-stock')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.safety-stock')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.status')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.total-warehouses')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.total-commerces')}</TableCell>
                  <TableCell align="center">{t('stock.catalog.total')}</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {catalog.length > 0 ? (
                  catalog.map((product) => {
                    const isExpanded = expandedId === product.globalProductId;
                    return (
                      <React.Fragment key={product.globalProductId}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              aria-label={t('stock.catalog.toggle-detail')}
                              onClick={() => setExpandedId(isExpanded ? null : product.globalProductId)}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {product.name}
                          </TableCell>
                          <TableCell className="unique-value-cell">{product.sku}</TableCell>
                          <TableCell>{formatMoneyAuto(product.averageCost)}</TableCell>
                          <TableCell align="center">{product.minimumStock ?? '-'}</TableCell>
                          <TableCell align="center">{product.safetyStock ?? '-'}</TableCell>
                          <TableCell align="center">
                            <span className={`status-pill status-${getStockStatus(product).toLowerCase()}`}>
                              {t(`stock.catalog.status-${getStockStatus(product).toLowerCase()}`)}
                            </span>
                          </TableCell>
                          <TableCell align="center" style={{ fontWeight: 600 }}>
                            {product.totalWarehouseStock}
                          </TableCell>
                          <TableCell align="center" style={{ fontWeight: 600 }}>
                            {product.totalCommerceStock}
                          </TableCell>
                          <TableCell align="center" style={{ fontWeight: 700 }}>
                            {product.totalStock}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              title={t('stock.global-products.table.view-movements')}
                              onClick={() => {
                                history.push(
                                  `/private/stock/movements?globalProductId=${product.globalProductId}&productName=${product.name}`,
                                );
                              }}
                            >
                              <RemoveRedEye />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={11} className="catalog-breakdown-cell">
                              {renderBreakdown(product)}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={11}>
                      {t('stock.catalog.no-results')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination && pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.currentPage - 1}
              onPageChange={(_, page: number) => handlePageChange(page + 1)}
              rowsPerPage={pagination.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
          )}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20 }}>
            <Card variant="outlined" style={{ maxWidth: 320, flex: '1 1 280px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('stock.catalog.total-inventory-value')}
                </Typography>
                <Typography variant="h5">{formatMoneyAuto(totalValueInventoryCpp)}</Typography>
                <Typography color="textSecondary" variant="caption">
                  {t('stock.catalog.cost-basis')}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined" style={{ maxWidth: 320, flex: '1 1 280px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('stock.catalog.products-with-alert')}
                </Typography>
                <Typography variant="h5">{summary.alert}</Typography>
                <Typography color="textSecondary" variant="caption">
                  {t('stock.catalog.alert-subtitle', { critical: summary.critical, minimum: summary.minimum })}
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined" style={{ maxWidth: 320, flex: '1 1 280px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('stock.catalog.products-ok')}
                </Typography>
                <Typography variant="h5">{summary.ok}</Typography>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </>
  );
};
